import {
  useWSSnackbar,
  WSButton,
  WSButtons,
  WSElement,
  WSFlexBox,
  WSForm,
  WSIconList,
  WSText
} from "@wingspanhq/fe-component-library";
import { ICollaboratorSchema } from "@wingspanhq/payments/dist/interfaces";
import React, { useState } from "react";
import { CSVLink } from "react-csv";
import { SetupFlowRouteComponentProps } from "../../components/SetupFlow/SetupFlow";
import { useBulkUploadCollaboratorsImporter } from "../../Invoices/screens/bulkUploadCollaborators/bulkUploadCollaboratorsImporter";
import { defaultCollaboratorGroupOption } from "../../Invoices/screens/bulkUploadCollaborators/BulkUploadCollaboratorsStep2";
import { useCreateBulkCollaboratorBatch } from "../../query/payments/mutations";
import {
  useCollaboratorsQuery,
  useListCollaboratorSettingsAdditionalData,
  useMissingInfoCollaboratorsQuery
} from "../../query/payments/queries";
import { WSQueries } from "../../query/WSQuery";
import { NEC1099FilingContainer } from "../components/NEC1099FilingContainer";
import { UploadCollaboratorWithFormW9 } from "./NEC1099FilingUploadCollaborators";

import styles from "./styles.module.scss";

export const NEC1099FilingMissingInfo: React.FC<SetupFlowRouteComponentProps> = ({
  onBack,
  onNext
}) => {
  const { openSnackbar } = useWSSnackbar();
  const [isFlatfileLoading, setIsFlatfileLoading] = useState(false);

  const missingInfoCollaboratorsQuery = useMissingInfoCollaboratorsQuery();
  const qCollaborators = useCollaboratorsQuery();

  const [
    createBulkCollaboratorBatch,
    createBulkCollaboratorBatchMeta
  ] = useCreateBulkCollaboratorBatch();
  const bulkUploadCollaboratorsImporter = useBulkUploadCollaboratorsImporter(
    defaultCollaboratorGroupOption,
    {
      onClose: () => {
        setIsFlatfileLoading(false);
      },
      withFormW9: true
    }
  );

  const onUploadSpreadsheet = async () => {
    setIsFlatfileLoading(true);
    const results = await bulkUploadCollaboratorsImporter();
    const csvCollaborators = results.validData as Array<
      UploadCollaboratorWithFormW9
    >;
    if (csvCollaborators.length === 0) {
      openSnackbar({
        duration: 5000,
        type: "warning",
        message:
          "Sorry! We could not find any collaborators in the file you uploaded"
      });
      return;
    }
    await createBulkCollaboratorBatch(
      {},
      {
        onSuccess: response => {
          setIsFlatfileLoading(false);
          onNext({
            bulkCollaboratorBatchId: response.bulkCollaboratorBatchId,
            csvCollaborators,
            selectedCollaboratorGroupId: defaultCollaboratorGroupOption.value,
            selectedCollaboratorGroupName: defaultCollaboratorGroupOption.label,
            fileName: results.fileName,
            flatFileBatchId: results.batchId
          });
        }
      }
    );
  };

  const addMissingInfoCollaboratorsToCsv = (
    collaborators: ICollaboratorSchema[]
  ) => {
    return collaborators.map(collaborator => ({
      email: collaborator.member.user.email,
      collaboratorId: collaborator.collaboratorId,
      contractorId: collaborator.clientData?.externalId,
      firstName: collaborator.member.user.profile?.firstName,
      lastName: collaborator.member.user.profile?.lastName,
      phoneNumber: (collaborator.labels?.phoneNumber as string) || "",

      w9FirstName: collaborator.formW9Data?.firstName || "",
      w9LastName: collaborator.formW9Data?.lastName || "",
      dob: collaborator.formW9Data?.dob,

      legalBusinessName: collaborator.formW9Data?.legalBusinessName || "",
      companyStructure: collaborator.formW9Data?.companyStructure || "",
      ein: collaborator.formW9Data?.ein || "",
      ssn: collaborator.formW9Data?.ssn || "",

      addressLine1: collaborator.formW9Data?.addressLine1 || "",
      addressLine2: collaborator.formW9Data?.addressLine2 || "",
      city: collaborator.formW9Data?.city || "",
      state: collaborator.formW9Data?.state || "",
      postalCode: collaborator.formW9Data?.postalCode || "",
      country: collaborator.formW9Data?.country || ""
    }));
  };

  return (
    <NEC1099FilingContainer title="Missing information">
      {/* TODO: Get total missing info collaborators */}
      <WSQueries queries={{ missingInfoCollaboratorsQuery }}>
        {({
          missingInfoCollaboratorsQuery: { data: missingInfoCollaborators }
        }) => {
          const downloadTemplateCsvProps = {
            filename: `Wingspan_Bulk_Upload_Missing_Info_Template.csv`,
            headers: [
              { label: "Email", key: "email" },
              { label: "Collaborator ID", key: "collaboratorId" },
              { label: "External Collaborator ID", key: "contractorId" }, // externalId
              { label: "First Name", key: "firstName" },
              { label: "Last Name", key: "lastName" },
              { label: "Phone number", key: "phoneNumber" },

              { label: "W-9 First Name", key: "w9FirstName" },
              { label: "W-9 Last Name", key: "w9LastName" },
              { label: "Date of Birth", key: "dob" },

              {
                label: "W-9 Legal Business Name",
                key: "legalBusinessName"
              },
              { label: "W-9 Company Structure", key: "companyStructure" },
              { label: "EIN", key: "ein" },
              { label: "SSN", key: "ssn" },

              { label: "Address Line1", key: "addressLine1" },
              { label: "Address Line2", key: "addressLine2" },
              { label: "City", key: "city" },
              { label: "State", key: "state" },
              { label: "Postal Code", key: "postalCode" },
              { label: "Country", key: "country" }
            ],
            data: addMissingInfoCollaboratorsToCsv(missingInfoCollaborators)
          };
          return (
            <>
              <WSText.Heading2 mb="L">
                {missingInfoCollaborators.length} of your collaborators are
                missing information required for filing with the IRS.
              </WSText.Heading2>
              <WSText.Paragraph color="grey700" mb="S">
                These collaborators may be missing required information like TIN
                or address. Wingspan can help import this information.
              </WSText.Paragraph>

              <WSText as="ol" mb="S" className={styles.bulletedList}>
                <WSText.LabelBold as="li">
                  1. Download CSV file including collaborators with missing
                  information.
                  <WSText.Paragraph color="grey700">
                    This is an editable list of your collaborators with missing
                    info.
                  </WSText.Paragraph>
                </WSText.LabelBold>

                <WSText.LabelBold as="li">
                  2. Fill in the missing information
                  <WSText.Paragraph color="grey700">
                    Find the blank cells and fill them. If you're missing any
                    information, you can always go back and upload additional
                    information later
                  </WSText.Paragraph>
                </WSText.LabelBold>

                <WSFlexBox mt="L" mb="M">
                  <WSElement mr="S" style={{ flex: 1 }}>
                    <WSText.Heading4 mb="S">
                      Included in template
                    </WSText.Heading4>
                    <WSIconList.Checks
                      items={[
                        "Wingspan Collaborator ID",
                        "Email",
                        "External ID",
                        "First Name",
                        "Last Name"
                      ]}
                      mb="M"
                    />
                  </WSElement>
                  <WSElement style={{ flex: 1 }}>
                    <WSText.Heading4 mb="S">
                      Possibly missing w-9 info
                    </WSText.Heading4>
                    <WSIconList
                      iconProps={{ name: "plus", color: "grey600" }}
                      items={[
                        "W-9 First Name",
                        "W-9 Last Name",
                        "Legal Business Name",
                        "Address",
                        "SSN",
                        "EIN"
                      ]}
                      mb="M"
                    />
                  </WSElement>
                </WSFlexBox>

                <WSText.LabelBold as="li">
                  3. Upload and import the spreadsheet to Wingspan
                  <WSText.Paragraph color="grey700">
                    Once you have saved the edited spreadsheet, upload it below.
                  </WSText.Paragraph>
                </WSText.LabelBold>
              </WSText>

              <WSButtons mt="XL">
                <CSVLink {...downloadTemplateCsvProps}>
                  <WSButton.Secondary fullWidth icon="download">
                    Download CSV
                  </WSButton.Secondary>
                </CSVLink>

                <WSButton.Primary
                  className={styles.actionBtn}
                  icon="upload"
                  data-testid="uploadMissingInfo"
                  onClick={onUploadSpreadsheet}
                  loading={
                    isFlatfileLoading ||
                    createBulkCollaboratorBatchMeta.isLoading
                  }
                >
                  Upload CSV
                </WSButton.Primary>
              </WSButtons>
              <WSFlexBox.Center mt="M">
                <WSButton.Link onClick={() => onNext({ skipForNow: true })}>
                  Skip for now
                </WSButton.Link>
              </WSFlexBox.Center>
            </>
          );
        }}
      </WSQueries>
    </NEC1099FilingContainer>
  );
};
